import React from 'react';

import { Badge, Group, Paper, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { SalesPortalAthleteRetrieve } from 'types';

import { AthleteInformationField } from './onFileFields';

interface OnFileSummaryProps {
  athlete: SalesPortalAthleteRetrieve;
  fields: Set<AthleteInformationField>;
  dateFormat: string;
  /** True when some visible fields are missing and render as inputs below the summary. */
  hasEditableFields: boolean;
}

const FIELD_LABELS: Record<Exclude<AthleteInformationField, 'firstName' | 'lastName'>, string> = {
  phone: 'Phone',
  dob: 'Date of birth',
  gender: 'Gender',
  address: 'Address',
  emergencyName: 'Emergency contact',
  emergencyPhone: 'Emergency phone',
  medicalNotes: 'Medical notes',
};

function formatAddress(athlete: SalesPortalAthleteRetrieve): string {
  const address = athlete.address;
  if (!address) return '';
  const regionAndZip = [address.region, address.zipCode].filter(Boolean).join(' ');
  return [address.addressLines, address.locality, regionAndZip].filter(Boolean).join(', ');
}

function fieldValue(
  field: AthleteInformationField,
  athlete: SalesPortalAthleteRetrieve,
  dateFormat: string,
): string {
  switch (field) {
    case 'phone':
      return athlete.phone ?? '';
    case 'dob':
      return athlete.dob ? dayjs(athlete.dob).format(dateFormat) : '';
    case 'gender':
      return athlete.gender ?? '';
    case 'address':
      return formatAddress(athlete);
    case 'emergencyName':
      return athlete.emergencyName ?? '';
    case 'emergencyPhone':
      return athlete.emergencyPhone ?? '';
    case 'medicalNotes':
      return athlete.medicalNotes ?? '';
    default:
      return '';
  }
}

const Row = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Group position='apart' align='start' noWrap spacing='md'>
    <Text size='sm' color='dimmed' sx={{ flexShrink: 0 }}>
      {label}
    </Text>
    <Text size='sm' align='right'>
      {value}
    </Text>
  </Group>
);

// The returning buyer's details as plain text — no inputs, no Edit button (spec Item 9):
// personal details are edited from the profile, never mid-checkout.
const OnFileSummary = ({
  athlete,
  fields,
  dateFormat,
  hasEditableFields,
}: OnFileSummaryProps): JSX.Element => {
  const name = `${athlete.firstName} ${athlete.lastName}`.trim();
  const detailFields = (Object.keys(FIELD_LABELS) as Array<keyof typeof FIELD_LABELS>).filter(field =>
    fields.has(field),
  );

  return (
    <Paper withBorder radius='md' p='md' data-cy='athleteInfoOnFileSummary'>
      <Stack spacing='xs'>
        <Group position='apart'>
          <Text fw={600}>Your details</Text>
          <Badge color='green' variant='light' data-cy='athleteInfoOnFileChip'>
            On file
          </Badge>
        </Group>
        {(fields.has('firstName') || fields.has('lastName')) && <Row label='Name' value={name} />}
        <Row label='Email' value={athlete.email} />
        {detailFields.map(field => (
          <Row key={field} label={FIELD_LABELS[field]} value={fieldValue(field, athlete, dateFormat)} />
        ))}
        <Text size='xs' color='dimmed' data-cy='athleteInfoOnFileNote'>
          To change them, update your profile first.
        </Text>
        {hasEditableFields && (
          <Text size='xs' color='dimmed' data-cy='athleteInfoEditableNote'>
            Fill in the missing details below — everything else is managed from your profile.
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

export default OnFileSummary;
